import React from "react";
import { ChangesetReason, ObjectClass } from '../../../../types';
import {
	Case,
	convertCase,
	designChangeMapSettingsGlobalLoopHandlingLabels,
	designChangeMapSettingsIntentHandlingLabels,
	designChangeMapSettingsLabels,
	isPositionOnlyChange
} from "../../../../utilities";
import { ChangeLogItem } from "./change-log-item";
import style from './change-log-item.module.scss';

function objectClassToName(className) {
	return className.replace(/NodeModel$/, '');
}

/**
 * JSDoc imports.
 *
 * @import {
 *   Design,
 *   DesignAppPropertyChangeItem,
 *   DesignApplicationVersionChangeItem,
 *   DesignCcxmlChangeItem,
 *   DesignChangeMapModule,
 *   DesignCompoundIntentChangeItem,
 *   DesignCsvDataChangeItem,
 *   DesignFillerPromptChangeItem,
 *   DesignFolderModuleChangeItem,
 *   DesignFunctionChangeItem,
 *   DesignGlobalIntentChangeItem,
 *   DesignGraphQlSchemaChangeItem,
 *   DesignIntentChangeItem,
 *   DesignLanguagesChangeItem,
 *   DesignModule,
 *   DesignModuleChangeItem,
 *   DesignModuleLinkChangeItem,
 *   DesignModuleNode,
 *   DesignObjectChangeItem,
 *   DesignUnitTestChangeItem,
 *   DesignVariableChangeItem
 * } from '@app/types';
 */
const dotProp = require('dot-prop');

/**
 * @typedef {Object} ChangesContainerProps
 * @property {string} listType - change key representing what kind of `changeMap` data was provided.
 * @property {unknown} changeMap - Usually an [id, changeItem] tuple, but for some changes (like settings), the changeItem is directly sent.
 * @property {Design} [serialized]
 * @property {boolean} [hidePositionChanges]
 * @property {Set<string>} [hiddenNames]
 * @property {(name: string) => void} [onHideName]
 */

/**
 * React component to display changeMap data based on two versions of design.json
 *
 * @class ChangesContainer
 * @extends {React.Component<ChangesContainerProps>}
 */
export class ChangesContainer extends React.Component {
	/** @type {Map<string, string>} */
	_displayNameCache = new Map();
	/** @type {DesignModuleLinkChangeItem[]} */
	_displayNameCacheLinks = null;

	constructor(props) {
		super(props);
		this.getChangeItem = this.getChangeItem.bind(this);
	}

	getHeaderByType() {
		switch (this.props.listType) {
			case "modules":
				return this.getModuleChangeHeader();
			default:
				break;
		}
	}

	getListByType() {
		switch (this.props.listType) {
			case "modules":
				return this.getModuleItems();
			case "application-modules":
			case "application-variables":
			case "application-objects":
			case "application-intents":
				return this.getApplicationComponentsItems();
			case "functions":
				return this.getFunctionsItems();
			case "folders":
				return this.getModuleFolderItems();
			case "fillerPrompts":
				return this.getFillerPromptsList();
			case "globalIntents":
				return this.getGlobalIntentsList();
			case 'compoundIntents':
				return this.getCompoundIntentsList();
			case "graphQLSchemas":
				return this.getGraphQLSchemasList();
			case "csvData":
				return this.getCSVDataList();
			case 'ccxml':
				return this.getCCXMLList();
			case 'appProperties':
				return this.getAppPropertiesItems();
			case "languages":
				return this.getLanguagesList();
			case "applicationVersion":
				return this.getApplicationVersionList();
			case "settings":
			case "globalErrorHandling":
			case "globalExceptionHandling":
			case "description":
			case "globalLoopHandling":
			case "inARow":
			case "xAndOut":
			case "intentHandling":
			case "inARowLimit":
			case "xAndOutLimit":
			case "omniChannel":	
			case "sensitiveOverrides":	
			case "noInputTimeout":
			case "dtmf_live_agent_override_pattern":
			case "rootSettings":
				return this.getSettingsList();
			case "unitTests":
				return this.getUnitTestsItems();
		}
	}

	/**
	 * Get name of deleted module from change maps
	 * 
	 * @param {string} modId
	 * @returns {string} module name 
	 */
	getDeletedApplicationsModuleName(modId) {
		const cmaps = window.apm.changeLogMaps || [];
		let name = 'UNKNOWN';

		cmaps.forEach(cm => {
			if (dotProp.get(cm.changeLogMap[0]['application-modules'])) {
				Object.keys(cm.changeLogMap[0]['application-modules']).forEach(key => {
					if (key === modId) {
						if (cm.changeLogMap[0]['application-modules'][key].reason === 'Deleted') {
							name = cm.changeLogMap[0]['application-modules'][key].node.name;
						}
					}
				});
			}
		});

		return name;
	}

	/**
	 * Get Module header
	 *
	 * @returns JSX
	 */
	getModuleChangeHeader() {
		/** @type {[string, DesignChangeMapModule]} */
		const [modId] = this.props.changeMap;
		// if module was deleted, then wont be on apm.getNode, find it in change maps
		const name = (
			window.apm.getNode(modId)?.name
			?? this.getDeletedApplicationsModuleName(modId)
		);

		return (
			<h5 key={modId} className="moduleHeader">
				<i className={"fe icon-package"} />
				<span style={{ paddingLeft: "6px" }} title={modId}>{name}</span>
			</h5>
		);
	}

	/**
	 * Generic list item for changelog UI
	 * @param {object} item data object 
	 */
	getChangeItem(props) {
		// Prefer stable ids from the underlying change item. When no id is
		// available, callers may pass `keyHint` for uniqueness within a list;
		// otherwise we fall back to (iconClass + name + changeLabel), which is
		// only safe when the parent renders a single child.
		const id = (
			props.item?.node?.id
			?? props.item?.link?.id
			?? props.item?.object?.id
			?? props.keyHint
			?? (props.iconClass + '-' + props.name + '-' + props.changeLabel)
		);
		const { keyHint, ...rest } = props;
		return (
			<ChangeLogItem {...rest} key={id} />
		);
	}

	getModuleItems() {
		/** @type {[string, DesignChangeMapModule]} */
		const [modId, modChange] = this.props.changeMap;
		/**
		 * @type {DesignModule}
		 * mod is the module object from the source design, or undefined when the
		 * module was deleted / no source design is available. In that case we fall
		 * back to rendering without source/target names for link items.
		 */
		const mod = this.props.serialized?.components.modules.find((m) => {
			return m.id === modId;
		});

		const noteName = (item) => {
			const text = (item.node.props.config.note ?? '').substring(0, 10);
			return `Note: ${text}`;
		};

		const { hidePositionChanges, hiddenNames = new Set(), onHideName } = this.props;

		const nodeItems = (
			(modChange.nodes ?? [])
			.map((item) => {
				const name = (
					item.node._class === ObjectClass.note
				) ? noteName(item) : item.node.name;
				return {
					// This must match the prefix used by objects with the same class + name combination.
					filterName: `${objectClassToName(item.node._class)} ${name}`,
					item,
					name
				};
			})
			.filter(({ filterName, item }) => (
				!(hidePositionChanges && isPositionOnlyChange(item.changeset))
				&& !hiddenNames.has(filterName)
			))
			.sort((a, b) => a.filterName.localeCompare(b.filterName))
			.map(({ filterName, item, name }) => {
				const changeLabel = item.reason;
				const iconClass = item.node.props.iconClass;

				return this.getChangeItem({
					item,
					name,
					iconClass,
					changeLabel,
					onHideName: onHideName ? () => onHideName(filterName) : undefined
				});
			})
		);

		let linkItems = [];
		const changedLinks = modChange.links;
		if (changedLinks) {
			/** @type {Map<string, DesignModuleNode>}*/
			const flowNodeMap = new Map();
			mod?.versions?.[1]?.flow?.nodes?.forEach((node) => {
				flowNodeMap.set(node.id, node);
			});

			if (this._displayNameCacheLinks !== changedLinks) {
				this._displayNameCache.clear();
				this._displayNameCacheLinks = changedLinks;
			}

			/**
			 * @param {DesignModuleLinkChangeItem} item
			 * @returns {string}
			 */
			const getCachedDisplayName = (item) => {
				const id = item.link.id;
				if (this._displayNameCache.has(id)) {
					return this._displayNameCache.get(id);
				}

				let displayName = item.link.props.config.name || "UN_NAMED";
				if (flowNodeMap.size > 0) {
					const source = flowNodeMap.get(item.link.source);
					const target = flowNodeMap.get(item.link.target);
					if (source && target) {
						displayName += " from " + source.name + " to " + target.name;
					} else if (source) {
						displayName += " from " + source.name;
					} else if (target) {
						displayName += " to " + target.name;
					}
				}
				this._displayNameCache.set(id, displayName);
				return displayName;
			};

			linkItems = (
				changedLinks
				.map((item) => {
					const name = getCachedDisplayName(item);
					return {
						filterName: `Link ${name}`,
						item,
						name
					};
				})
				.filter(({ filterName, item }) => (
					!(hidePositionChanges && isPositionOnlyChange(item.changeset))
					&& !hiddenNames.has(filterName)
				))
				.sort((a, b) => a.name.localeCompare(b.name))
				.map(({ filterName, item, name }) => {
					const changeLabel = item.reason;
					const iconClass = "link";

					return this.getChangeItem({
						item,
						name,
						iconClass,
						changeLabel,
						onHideName: onHideName ? () => onHideName(filterName) : undefined
					});
				})
			);
		}

		const localVariableItems = (
			(modChange.localVariables ?? [])
			.map((item) => {
				const name = item.object.name || "UN_NAMED";
				return {
					filterName: `Local Variable ${name}`,
					item,
					name
				};
			})
			.filter(({ filterName }) => !hiddenNames.has(filterName))
			.sort((a, b) => a.name.localeCompare(b.name))
			.map(({ filterName, item, name }) => {
				const changeLabel = item.reason;
				const iconClass = item.object.props.iconClass;

				return this.getChangeItem({
					item,
					name,
					iconClass,
					changeLabel,
					onHideName: onHideName ? () => onHideName(filterName) : undefined
				});
			})
		);

		return nodeItems.concat(linkItems).concat(localVariableItems);
	}

	getApplicationComponentsItems() {
		const {
			changeMap,
			hiddenNames = new Set(),
			listType,
			onHideName
		} = this.props;
		/** @type {DesignIntentChangeItem | DesignModuleChangeItem | DesignObjectChangeItem | DesignVariableChangeItem} */
		const item = changeMap[1];
		const name = item.node.name || "UN_NAMED";
		const changeLabel = item.reason;
		const iconClass = item.node.props.iconClass;
		const typePrefix = (listType !== "application-objects") ? (
			// Converts keys like "application-variables" to "Variable".
			convertCase(listType.replace(/^application-|s$/, ''), Case.title)
		) : (
			// This must match the prefix used by nodes with the same class + name combination.
			objectClassToName(item.node._class)
		);
		const filterName = `${typePrefix} ${name}`;

		return (hiddenNames.has(filterName)) ? null : this.getChangeItem({
			item,
			name,
			iconClass,
			changeLabel,
			onHideName: onHideName ? () => onHideName(filterName) : undefined
		});
	}

	getFunctionsItems() {
		/** @type {DesignFunctionChangeItem} */
		const item = this.props.changeMap[1];
		const name = item.object.name || "UN_NAMED";
		const changeLabel = item.reason;

		return this.getChangeItem({
			item,
			name,
			iconClass: null,
			changeLabel
		});
	}

	getModuleFolderItems() {
		/** @type {DesignFolderModuleChangeItem} */
		const item = this.props.changeMap[1];
		const name = item.object.name || "UN_NAMED";
		const changeLabel = item.reason;

		return this.getChangeItem({
			item,
			name,
			iconClass: null,
			changeLabel
		});
	}

	getGlobalIntentsList() {
		/** @type {DesignGlobalIntentChangeItem} */
		const item = this.props.changeMap[1];
		var name = "UN_NAMED";
		var iconClass = "radio";
		if (item.object.conditionProperties) {
			name = item.object.conditionProperties.props.config.name || "UN_NAMED";
			iconClass = "link";
		} else if (item.object.intent) {
			const intent = window.apm.serialized.components.intents.find((i) => i.id === item.object.intent);
			name = (intent) ? intent.name : "UN_NAMED";
			iconClass = "radio";
		}
		const changeLabel = item.reason;

		return this.getChangeItem({
			item,
			name,
			iconClass: iconClass,
			changeLabel
		});
	}

	getCompoundIntentsList() {
		/** @type {DesignCompoundIntentChangeItem} */
		const item = this.props.changeMap[1];
		const { intents } = window.apm.serialized.components;
		const fromIntent1 = intents.find(i => i.id === item.object.fromIntent1);
		const fromIntent2 = intents.find(i => i.id === item.object.fromIntent2);
		const toIntent = intents.find(i => i.id === item.object.toIntent);
		const changeLabel = item.reason;

		return (<div>
			<table style={{ width: "98%", margin: "10px" }} >
				<thead>
					<tr>
						<th style={{width: '25%'}}>From Intent 1</th>
						<th style={{width: '25%'}} >From Intent 2</th>
						<th style={{width: '25%'}} >To Intent</th>
						<th style={{width: '25%'}} >Reason</th>
					</tr>
				</thead>
				<tbody>
					<tr >
						<td>{fromIntent1 ? fromIntent1.name : "???"}</td>
						<td>{fromIntent2 ? fromIntent2.name : "???"}</td>
						<td>{toIntent ? toIntent.name : "???"}</td>
						<td>{changeLabel}</td>
					</tr>

				</tbody>
			</table>

		</div>)
		// TODO:
		//return this.getChangeItem({ name, iconClass: null, changeLabel });
	}

	getFillerPromptsList() {
		/** @type {[string, Record<string, DesignFillerPromptChangeItem>]} */
		const [fillerType, fillerPromptItems] = this.props.changeMap;

		return (
			<div
				style={{ margin: "0 20px" }}
				key={fillerType}>
				<div style={{ padding: "4px", width: "80%" }}>
					<i className={"fe icon-message-square"} />
					<span style={{ paddingLeft: "4px" }}>{fillerType.toUpperCase()}</span>
				</div>
				{Object.entries(fillerPromptItems).map(([fillerPromptName, item]) => {
					const name = item.object.name || "UN_NAMED";
					const changeLabel = item.reason;

					return this.getChangeItem({
						item: null,
						name,
						iconClass: null,
						changeLabel,
						keyHint: fillerPromptName
					});
				})}
			</div>
		);
	}

	getGraphQLSchemasList() {
		/** @type {DesignGraphQlSchemaChangeItem} */
		const item = this.props.changeMap[1];
		const name = item.object.name || "UN_NAMED";
		const changeLabel = item.reason;

		return this.getChangeItem({
			item: null,
			name,
			iconClass: null,
			changeLabel
		});
	}

	getCSVDataList() {
		/** @type {DesignCsvDataChangeItem} */
		const item = this.props.changeMap[1];
		const name = item.object.name || "UN_NAMED";
		const changeLabel = item.reason;

		return this.getChangeItem({
			item: null,
			name,
			iconClass: null,
			changeLabel
		});
	}

	getCCXMLList() {
		/** @type {DesignCcxmlChangeItem} */
		const item = this.props.changeMap[1];
		const name = "CCXML";
		const changeLabel = item.reason;

		return this.getChangeItem({
			item: null,
			name,
			iconClass: null,
			changeLabel
		});
	}

	getAppPropertiesItems() {
		/** @type {DesignAppPropertyChangeItem} */
		const item = this.props.changeMap[1];
		const name = item.object.config || "UN_NAMED";
		const changeLabel = item.reason;

		return this.getChangeItem({
			item: null,
			name,
			iconClass: null,
			changeLabel
		});
	}

	getLanguagesList() {
		/** @type {DesignLanguagesChangeItem} */
		const item = this.props.changeMap[1];
		const name = "Languages";
		const changeLabel = item.reason;

		return this.getChangeItem({
			item: null,
			name,
			iconClass: null,
			changeLabel
		});
	}

	getApplicationVersionList() {
		/** @type {DesignApplicationVersionChangeItem} */
		const item = this.props.changeMap[1];
		const name = "Dependency Version";
		const changeLabel = item.reason;

		return this.getChangeItem({
			item: null,
			name,
			iconClass: null,
			changeLabel
		});
	}
	
	getSettingsList() {
		const getName = (key) => {
			if (key in designChangeMapSettingsLabels) {
				return designChangeMapSettingsLabels[key];
			} else if (key in designChangeMapSettingsIntentHandlingLabels) {
				return designChangeMapSettingsIntentHandlingLabels[key];
			} else if (key in designChangeMapSettingsGlobalLoopHandlingLabels) {
				return designChangeMapSettingsGlobalLoopHandlingLabels[key];
			}
		}
		return this.getChangeItem({
			name: getName(this.props.listType),
			iconClass: null,
			changeLabel: ChangesetReason.modified,
		});		
	}

	getUnitTestsItems() {
		/** @type {DesignUnitTestChangeItem} */
		const item = this.props.changeMap[1];
		const { hiddenNames = new Set(), onHideName } = this.props;
		const name = item.object.name || "UN_NAMED";
		const changeLabel = item.reason;

		const filterName = `Unit Test ${name}`;

		return (hiddenNames.has(filterName)) ? null : this.getChangeItem({
			item,
			name,
			iconClass: null,
			changeLabel,
			onHideName: onHideName ? () => onHideName(filterName) : undefined
		});
	}

	render() {
		const list = this.getListByType();
		const emptyList = (
			list == null
			|| (Array.isArray(list) && list.length === 0)
		);

		return (emptyList) ? null : (
			<div className={style.changeLogSet}>
				{this.getHeaderByType()}
				{list}
			</div>
		);
	}
}

export default ChangesContainer;
